/**
 * Seating Map - RWC 2027
 * Legend-based ticket filtering for Rugby World Cup 2027 venues
 * Supports interactive SVG maps for all RWC 2027 venues
 *
 * Categories (4 purchasable tiers):
 * - Cat 1 (purple #9B59B6): Premium sideline
 * - Cat 2 (teal #2DD4BF): Good sideline/corners
 * - Cat 3 (pink #F472B6): Behind goals/upper
 * - Cat 4/5/6 (orange #F97316): Furthest sections (combined)
 *
 * 2026-10-01: section-level schematic maps (svg.gt-map). Each section is <g class="sec" data-sec="112"
 * data-name="Section 112" data-categories="1,2" data-section="category-1" data-category="category-1">.
 * A section can belong to several categories (rows vary), so highlighting lights every section whose
 * data-categories contains the chosen group, recoloured to that group's colour (svg[data-hl]).
 * Older maps (data-category groups only) keep working unchanged.
 *
 * 2026-10-15 (blockmap): product pages link seat listings to their map section. Cards for listings titled
 * "Category N - Block X Row Y" carry data-block="x" (lowercased) and data-qty. Sections with their own listings
 * get .has-tix, a price pill and keyboard focus; clicking one filters the panel to that section (its listings
 * first, then the generic cards of the same category group). Hovering a card lights its section.
 */

(function() {
  'use strict';

  // 2026-10-06 (es_translate_oct6b): optional UI strings. The Spanish venue-page copies of the seating-map sections
  // carry a JSON dictionary in data-i18n on .seating-map-section; without it (English pages and all product pages)
  // every string falls back to the original English text.
  var I18N = null;
  function t(key, en) {
    if (I18N === null) {
      I18N = {};
      try { var el = document.querySelector('.seating-map-section[data-i18n]'); if (el) I18N = JSON.parse(el.getAttribute('data-i18n')) || {}; } catch (e) { I18N = {}; }
    }
    return Object.prototype.hasOwnProperty.call(I18N, key) ? I18N[key] : en;
  }

  // Helper function to format prices in USD or EUR
  function formatMapPrice(dollars) {
    if (window.CurrencyConverter && window.CurrencyConverter.isEurMode()) {
      return window.CurrencyConverter.convertDollars(dollars);
    }
    return '$' + Math.round(dollars).toLocaleString('en-US');
  }

  // Map Tixstock SVG categories to GoalTickets groups
  var SVG_TO_GROUP = {
    'category-1': 'cat-1',
    'category-2': 'cat-2',
    'category-3': 'cat-3',
    'category-4': 'cat-456',
    'category-5': 'cat-456',
    'category-6': 'cat-456',
    'standing': 'cat-456'
  };

  var GROUP_TO_SVG = {
    'cat-1': ['category-1'],
    'cat-2': ['category-2'],
    'cat-3': ['category-3'],
    'cat-456': ['category-4', 'category-5', 'category-6', 'standing']
  };

  var GROUP_LABELS = {
    'cat-1': 'Category 1 \u2014 Premium Sideline',
    'cat-2': 'Category 2 \u2014 Sideline & Corner',
    'cat-3': 'Category 3 \u2014 Behind Posts',
    'cat-456': 'Category 4/5/6 \u2014 Upper Level'
  };

  // Section-level maps: which data-categories tokens each legend group lights up
  var GROUP_TOKENS = {
    'cat-1': ['1'],
    'cat-2': ['2'],
    'cat-3': ['3'],
    'cat-456': ['4', '5', '6', 'standing'],
    'cat-4': ['4', '5', '6', 'standing']
  };
  // legacy alias: an older Townsville legend used "cat-4" for the lowest group
  GROUP_TO_SVG['cat-4'] = GROUP_TO_SVG['cat-456'];

  function sectionCats(el) {
    var v = el && el.getAttribute('data-categories');
    if (v === null || v === undefined) return null;
    return v ? v.split(',') : [];
  }

  function catRangeText(nums) {
    if (nums.length < 2) return nums.join('');
    var consecutive = true;
    for (var i = 1; i < nums.length; i++) { if (+nums[i] !== +nums[i - 1] + 1) consecutive = false; }
    return consecutive ? nums[0] + '-' + nums[nums.length - 1] : nums.join(', ');
  }

  class SeatingMapRWC2027 {
    constructor() {
      this.ticketsPanel = document.getElementById('seating-map-tickets');
      this.legendItems = document.querySelectorAll('.legend-item');

      if (this.legendItems.length === 0) return;
      // legacy legend key "cat-4" (old Townsville section) is the same group as "cat-456"
      Array.prototype.forEach.call(this.legendItems, function(it) {
        if (it.dataset.category === 'cat-4') it.dataset.category = 'cat-456';
      });
      // Page mode (venue seating-map pages, request.page_type == 'page'): no ticket panel is rendered,
      // so only legend + SVG highlighting/tooltips are wired up and no "No tickets available" state exists.
      this.pageMode = !this.ticketsPanel;

      this.activeGroup = null;
      this.priceRanges = {};

      // SVG elements (present for venues with interactive Tixstock maps)
      this.svgEl = document.querySelector('.seating-map-svg');
      this.svgCategoryGroups = this.svgEl
        ? Array.from(this.svgEl.querySelectorAll('[data-category]:not([data-category="field"])'))
        : [];
      this.svgSections = this.svgEl
        ? Array.from(this.svgEl.querySelectorAll('[data-category]:not([data-category="field"]) .addedfill'))
        : [];
      this.tooltip = document.getElementById('map-tooltip');
      this.hasSvg = this.svgSections.length > 0;
      // Section-level schematic map (sections carry data-sec + data-categories)
      this.richMap = this.hasSvg && !!(this.svgEl && this.svgEl.querySelector('[data-sec][data-categories]'));
      // block-level state (product pages with a section-level map)
      this.activeBlock = null;
      this.blockIndex = {};
      this.secByKey = {};

      this.init();
      if (this.richMap) this.setupExpand();
    }

    init() {
      if (this.pageMode) {
        this.bindLegendEvents();
        if (this.hasSvg) this.bindSvgEvents();
        return;
      }
      this.calculatePriceRanges();
      this.hideSoldOutCategories();
      this.showAllTickets();
      this.setupSortControls();
      this.hideDuplicateTicketSection();
      this.bindLegendEvents();
      if (this.hasSvg) this.bindSvgEvents();
      if (this.richMap) this.setupBlocks();

      // Currency converter: recalculate price ranges when EUR rate is ready
      var self = this;
      document.addEventListener('currency:ready', () => {
        self.calculatePriceRanges();
      });
    }

    calculatePriceRanges() {
      var ticketCards = this.getOriginalTicketCards();

      this.priceRanges = {
        'cat-1': { min: Infinity, max: -Infinity, count: 0 },
        'cat-2': { min: Infinity, max: -Infinity, count: 0 },
        'cat-3': { min: Infinity, max: -Infinity, count: 0 },
        'cat-456': { min: Infinity, max: -Infinity, count: 0 }
      };

      var self = this;
      ticketCards.forEach(function(card) {
        var cardText = card.textContent;
        var group = self.detectCardGroup(cardText);
        if (!group) return;

        var priceEl = card.querySelector('.price-value');
        if (priceEl) {
          var priceText = priceEl.textContent.replace(/[^0-9.,]/g, '').replace(',', '');
          var price = parseFloat(priceText);
          if (!isNaN(price)) {
            self.priceRanges[group].min = Math.min(self.priceRanges[group].min, price);
            self.priceRanges[group].max = Math.max(self.priceRanges[group].max, price);
            self.priceRanges[group].count++;
          }
        }
      });
    }

    detectCardGroup(cardText) {
      var text = cardText.toLowerCase();

      if (/category\s*4\s*\/\s*5\s*\/\s*6/i.test(text) || /cat\s*4\s*\/\s*5\s*\/\s*6/i.test(text)) {
        return 'cat-456';
      }
      if (/category\s*3(?:\s|$|,)/i.test(text) || /cat\s*3(?:\s|$|,)/i.test(text)) {
        return 'cat-3';
      }
      if (/category\s*2(?:\s|$|,)/i.test(text) || /cat\s*2(?:\s|$|,)/i.test(text)) {
        return 'cat-2';
      }
      if ((/category\s*1(?:\s|$|,)/i.test(text) || /cat\s*1(?:\s|$|,)/i.test(text)) && !/cat\s*1\d/i.test(text)) {
        return 'cat-1';
      }
      return null;
    }

    hideSoldOutCategories() {
      var ticketCards = this.getOriginalTicketCards();
      var groupsWithTickets = new Set();

      var self = this;
      ticketCards.forEach(function(card) {
        var group = self.detectCardGroup(card.textContent);
        if (group) groupsWithTickets.add(group);
      });

      this.legendItems.forEach(function(item) {
        var category = item.dataset.category;
        if (!groupsWithTickets.has(category)) {
          item.style.display = 'none';
        } else {
          self.addPriceToLegendItem(item, category);
        }
      });
    }

    addPriceToLegendItem(item, groupKey) {
      var priceRange = this.priceRanges[groupKey];
      if (!priceRange || priceRange.count === 0) return;
      if (item.querySelector('.legend-price')) return;

      var priceEl = document.createElement('span');
      priceEl.className = 'legend-price';

      if (priceRange.min === priceRange.max) {
        priceEl.textContent = formatMapPrice(priceRange.min);
      } else {
        priceEl.textContent = formatMapPrice(priceRange.min) + '+';
      }

      item.appendChild(priceEl);
    }

    hideDuplicateTicketSection() {
      var duplicateSection = document.querySelector('.ticket-selection-wrapper');
      if (duplicateSection) {
        duplicateSection.style.display = 'none';
      }
      var marketplaceSection = document.querySelector('.ticket-variants-marketplace-section');
      if (marketplaceSection) {
        marketplaceSection.style.display = 'none';
      }
    }

    bindLegendEvents() {
      var self = this;

      this.legendItems.forEach(function(item) {
        item.setAttribute('tabindex', '0');
        item.setAttribute('role', 'button');

        var handleActivate = function() {
          var category = item.dataset.category;
          if (self.activeGroup === category) {
            self.clearSelection();
          } else {
            self.selectGroup(category);
          }
        };

        item.addEventListener('click', handleActivate);

        item.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleActivate();
          }
        });
      });

      document.addEventListener('click', function(e) {
        if (!e.target.closest('.legend-item') &&
            !e.target.closest('.seating-map-tickets') &&
            !e.target.closest('.ticket-sort-bar') &&
            !e.target.closest('.seating-map-svg') &&
            !e.target.closest('.map-tooltip')) {
          self.clearSelection();
        }
      });
    }

    // ===== SVG INTERACTIVITY =====

    bindSvgEvents() {
      var self = this;

      this.svgSections.forEach(function(section) {
        // Section-level maps are role="img" with 100+ sections: keyboard users filter via the legend chips
        // instead of tabbing through every section, so sections are not made focusable there.
        if (!self.richMap) {
          section.setAttribute('tabindex', '0');
          section.setAttribute('role', 'button');
        }

        var getCategoryGroup = function() {
          var categoryGroup = section.closest('[data-category]');
          if (!categoryGroup) return null;
          var svgCat = categoryGroup.getAttribute('data-category');
          return SVG_TO_GROUP[svgCat] || null;
        };

        section.addEventListener('mouseenter', function(e) {
          var group = getCategoryGroup();
          if (self.richMap) { self.showSectionTooltip(section.closest('[data-sec]'), group, e); return; }
          if (group) self.showSvgTooltip(group, e);
        });

        section.addEventListener('mousemove', function(e) {
          self.moveSvgTooltip(e);
        });

        section.addEventListener('mouseleave', function() {
          self.hideSvgTooltip();
        });

        section.addEventListener('focus', function() {
          var group = getCategoryGroup();
          if (group) self.showSvgTooltipForElement(group, section);
        });

        section.addEventListener('blur', function() {
          self.hideSvgTooltip();
        });

        var handleSelect = function(e) {
          e.stopPropagation();
          if (self.richMap && !self.pageMode) {
            var bkey = self.secKey(section.closest('[data-sec]'));
            if (bkey && self.blockIndex[bkey]) {
              if (self.activeBlock === bkey) self.clearSelection(); else self.selectBlock(bkey);
              return;
            }
          }
          var group = getCategoryGroup();
          if (group) {
            if (self.activeGroup === group) {
              self.clearSelection();
            } else {
              self.selectGroup(group);
            }
          }
        };

        section.addEventListener('click', handleSelect);

        section.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelect(e);
          }
        });
      });
    }

    highlightSvgSections(groupKey) {
      if (!this.hasSvg) return;
      var svgCats = GROUP_TO_SVG[groupKey] || [];
      var tokens = GROUP_TOKENS[groupKey] || [];
      if (this.richMap && this.svgEl) this.svgEl.setAttribute('data-hl', groupKey);

      this.svgCategoryGroups.forEach(function(g) {
        var cat = g.getAttribute('data-category');
        var cats = sectionCats(g);
        var lit = cats
          ? cats.some(function(c) { return tokens.indexOf(c) >= 0; })
          : svgCats.indexOf(cat) >= 0;
        if (lit) {
          g.classList.add('svg-active');
          g.classList.remove('svg-dimmed');
        } else {
          g.classList.add('svg-dimmed');
          g.classList.remove('svg-active');
        }
      });
    }

    clearSvgHighlights() {
      if (!this.hasSvg) return;
      if (this.svgEl) this.svgEl.removeAttribute('data-hl');
      this.svgCategoryGroups.forEach(function(g) {
        g.classList.remove('svg-active', 'svg-dimmed');
      });
    }

    // "Section 112 · Level 1" / "Cat 1 (rows vary: Cat 1-2) — from $X"
    showSectionTooltip(secEl, group, event) {
      if (!this.tooltip || !secEl) return;
      var esc = function(s) { return String(s).replace(/[&<>"]/g, function(ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]; }); };
      var name = secEl.getAttribute('data-name') || (t('section', 'Section ') + secEl.getAttribute('data-sec'));
      var lvlEl = secEl.closest('[data-level-name]');
      var head = name + (lvlEl ? ' · ' + lvlEl.getAttribute('data-level-name') : '');
      var cats = sectionCats(secEl) || [];
      var nums = cats.filter(function(c) { return /^\d+$/.test(c); });
      var line2;
      if (nums.length) {
        line2 = t('cat', 'Cat ') + nums[0];
        if (nums.length > 1) line2 += t('rows_vary', ' (rows vary: Cat ') + catRangeText(nums) + ')';
        if (cats.indexOf('af') >= 0) line2 += t('af_rows', ' · alcohol-free rows');
      } else if (cats.indexOf('af') >= 0) {
        line2 = t('af_section', 'Alcohol-free section');
      } else if (cats.indexOf('standing') >= 0) {
        line2 = t('standing', 'Standing (general admission)');
      } else {
        line2 = t('not_published', 'Category not published for the sample match');
      }
      var priceText = '';
      var pr = group ? this.priceRanges[group] : null;
      if (!this.pageMode && pr && pr.count > 0) {
        priceText = t('from', ' — from ') + '<span class="tooltip-price">' + formatMapPrice(pr.min) + '</span>';
      }
      var bi = !this.pageMode && this.blockIndex ? this.blockIndex[this.secKey(secEl)] : null;
      if (bi) {
        // the section has its own listings: show their category, price and quantity
        if (bi.cats.length) line2 = t('cat', 'Cat ') + catRangeText(bi.cats.slice().sort());
        priceText = ' · <span class="tooltip-price">' + formatMapPrice(bi.min) + '</span>' +
          (bi.qty ? ' · ' + bi.qty + ' ' + t('left', 'left') : '') +
          '<br><span class="gt-tip-hint">' + esc(t('click_section', 'Click to see these seats')) + '</span>';
      }
      this.tooltip.innerHTML = '<strong>' + esc(head) + '</strong><br>' + esc(line2) + priceText;
      this.tooltip.classList.add('visible');
      if (event) this.moveSvgTooltip(event);
    }

    // Enlarge button: shows the same (live) map full-screen so section numbers are readable on product
    // pages (map height is capped there) and on phones (wide, scrollable). The wrapper node is moved, not
    // cloned, so highlighting / filtering keep working; it is put back on close.
    setupExpand() {
      var self = this;
      var wrapper = this.svgEl.closest('.seating-map-wrapper');
      if (!wrapper || wrapper.querySelector('.gt-map-expand')) return;
      if (!document.getElementById('gt-map-expand-css')) {
        var st = document.createElement('style');
        st.id = 'gt-map-expand-css';
        st.textContent =
          '.gt-map-expand{position:absolute;top:10px;right:10px;z-index:5;display:inline-flex;align-items:center;gap:6px;padding:6px 10px;border:1px solid #d1d5db;border-radius:8px;background:rgba(255,255,255,.95);color:#111827;font:600 12.5px/1 Arial,Helvetica,sans-serif;cursor:pointer;box-shadow:0 1px 3px rgba(0,0,0,.12)}' +
          '.gt-map-expand:hover{background:#f3f4f6}.gt-map-expand svg{width:14px;height:14px}' +
          // theme mobile-ux-fixes.css forces buttons to position:relative !important
          '.seating-map-wrapper .gt-map-expand{position:absolute!important;z-index:5!important;top:8px!important;right:8px!important;margin:0!important;width:auto!important;height:auto!important;min-height:0!important;min-width:0!important;padding:5px 9px!important;font:600 12px/1 Arial,Helvetica,sans-serif!important;background:rgba(255,255,255,.95)!important;color:#111827!important;border:1px solid #d1d5db!important;border-radius:8px!important}' +
          '.gt-map-overlay .gt-map-close{position:absolute!important;z-index:6!important;top:10px!important;right:10px!important;margin:0!important;width:auto!important;height:auto!important;min-height:0!important;padding:8px 12px!important;font:600 13px/1 Arial,Helvetica,sans-serif!important;background:#111827!important;color:#ffffff!important;border:0!important;border-radius:8px!important}' +
          '@media (max-width:768px){.seating-map-wrapper .gt-map-expand{top:3px!important;right:3px!important;padding:5px!important;font-size:11px!important}.seating-map-wrapper .gt-map-expand span{display:none!important}}' +
          '.gt-map-overlay{position:fixed;inset:0;z-index:2147483000;background:rgba(15,23,42,.72);display:flex;align-items:center;justify-content:center;padding:12px}' +
          '.gt-map-overlay .seating-map-wrapper{width:min(1400px,100%);max-height:100%;display:flex;flex-direction:column;background:#fff;overflow:hidden;position:relative}' +
          '.gt-map-overlay .seating-map-scroll-container{max-height:none!important;overflow:auto;flex:1 1 auto;-webkit-overflow-scrolling:touch}' +
          '.gt-map-overlay .seating-map-svg{max-height:calc(100vh - 150px)!important;width:100%;margin:0 auto}' +
          '.gt-map-overlay .gt-map-expand{display:none}' +
          '.gt-map-close{position:absolute;top:10px;right:10px;z-index:6;padding:8px 12px;border:0;border-radius:8px;background:#111827;color:#fff;font:600 13px/1 Arial,Helvetica,sans-serif;cursor:pointer}' +
          'html.gt-map-locked,html.gt-map-locked body{overflow:hidden!important}' +
          '@media (max-width:768px){.gt-map-overlay{padding:0}.gt-map-overlay .seating-map-wrapper{height:100%;border-radius:0}' +
          '.gt-map-overlay .seating-map-svg{width:1000px!important;max-width:none!important;max-height:none!important}}';
        document.head.appendChild(st);
      }
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gt-map-expand';
      btn.setAttribute('aria-label', t('enlarge_label', 'Enlarge seating map'));
      btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg><span>' + t('enlarge', 'Enlarge') + '</span>';
      wrapper.appendChild(btn);

      var placeholder = null, overlay = null, closeBtn = null;
      var onKey = function(e) { if (e.key === 'Escape') close(); };
      var open = function() {
        if (overlay) return;
        placeholder = document.createComment('gt-map-placeholder');
        wrapper.parentNode.insertBefore(placeholder, wrapper);
        overlay = document.createElement('div');
        overlay.className = 'gt-map-overlay';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-label', t('enlarged_label', 'Seating map (enlarged)'));
        closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'gt-map-close';
        closeBtn.textContent = t('close', 'Close ×');
        closeBtn.addEventListener('click', function(e) { e.stopPropagation(); close(); });
        overlay.addEventListener('click', function(e) { if (e.target === overlay) close(); });
        overlay.appendChild(wrapper);
        wrapper.appendChild(closeBtn);
        document.body.appendChild(overlay);
        document.documentElement.classList.add('gt-map-locked');
        document.addEventListener('keydown', onKey);
        self.hideSvgTooltip();
        // phones: the enlarged map is wider than the screen, start centred on the pitch
        var sc = wrapper.querySelector('.seating-map-scroll-container');
        if (sc && sc.scrollWidth > sc.clientWidth) sc.scrollLeft = (sc.scrollWidth - sc.clientWidth) / 2;
        if (sc && sc.scrollHeight > sc.clientHeight) sc.scrollTop = (sc.scrollHeight - sc.clientHeight) / 2;
        closeBtn.focus();
      };
      var close = function() {
        if (!overlay) return;
        document.removeEventListener('keydown', onKey);
        if (closeBtn && closeBtn.parentNode) closeBtn.parentNode.removeChild(closeBtn);
        var sc2 = wrapper.querySelector('.seating-map-scroll-container');
        if (sc2) { sc2.scrollLeft = 0; sc2.scrollTop = 0; }
        placeholder.parentNode.insertBefore(wrapper, placeholder);
        placeholder.parentNode.removeChild(placeholder);
        overlay.parentNode.removeChild(overlay);
        overlay = null;
        document.documentElement.classList.remove('gt-map-locked');
        self.hideSvgTooltip();
        btn.focus();
      };
      btn.addEventListener('click', function(e) { e.stopPropagation(); open(); });
    }

    showSvgTooltipForElement(group, element) {
      if (!this.tooltip) return;

      var label = t('g_' + group, GROUP_LABELS[group]) || group;
      var priceText = '';
      var pr = this.priceRanges[group];
      if (pr && pr.count > 0) {
        priceText = t('from', ' \u2014 from ') + '<span class="tooltip-price">' + formatMapPrice(pr.min) + '</span>';
      }

      this.tooltip.innerHTML = label + priceText;
      this.tooltip.classList.add('visible');

      var wrapper = this.tooltip.closest('.seating-map-wrapper');
      if (!wrapper) return;

      var wrapperRect = wrapper.getBoundingClientRect();
      var elRect = element.getBoundingClientRect();
      var x = elRect.left + elRect.width / 2 - wrapperRect.left;
      var y = elRect.top - wrapperRect.top;

      this.tooltip.style.left = x + 'px';
      this.tooltip.style.top = (y - 50) + 'px';
    }

    showSvgTooltip(group, event) {
      if (!this.tooltip) return;

      var label = t('g_' + group, GROUP_LABELS[group]) || group;
      var priceText = '';
      var pr = this.priceRanges[group];
      if (pr && pr.count > 0) {
        priceText = t('from', ' \u2014 from ') + '<span class="tooltip-price">' + formatMapPrice(pr.min) + '</span>';
      }

      this.tooltip.innerHTML = label + priceText;
      this.tooltip.classList.add('visible');
      this.moveSvgTooltip(event);
    }

    moveSvgTooltip(event) {
      if (!this.tooltip) return;
      var wrapper = this.tooltip.closest('.seating-map-wrapper');
      if (!wrapper) return;

      var rect = wrapper.getBoundingClientRect();
      var x = event.clientX - rect.left;
      var y = event.clientY - rect.top;

      if (this.richMap) {
        // two-line tooltip: keep it above the pointer and inside the map box
        var tw = this.tooltip.offsetWidth || 0;
        var half = tw / 2;
        if (half && rect.width > tw) x = Math.min(Math.max(x, half + 4), rect.width - half - 4);
        this.tooltip.style.left = x + 'px';
        this.tooltip.style.top = Math.max(4, y - (this.tooltip.offsetHeight || 40) - 14) + 'px';
        return;
      }
      this.tooltip.style.left = x + 'px';
      this.tooltip.style.top = (y - 50) + 'px';
    }

    hideSvgTooltip() {
      if (!this.tooltip) return;
      this.tooltip.classList.remove('visible');
    }

    // ===== SELECTION & FILTERING =====

    selectGroup(groupKey) {
      this.clearBlockState();
      this.activeGroup = groupKey;

      this.legendItems.forEach(function(item) {
        item.classList.toggle('active', item.dataset.category === groupKey);
      });

      this.highlightSvgSections(groupKey);
      this.showGroupTickets(groupKey);
    }

    clearSelection() {
      this.activeGroup = null;
      this.clearBlockState();

      this.legendItems.forEach(function(item) {
        item.classList.remove('active');
      });

      this.clearSvgHighlights();
      this.showAllTickets();
    }

    matchGroup(cardText, groupKey) {
      var text = cardText.toLowerCase();

      if (groupKey === 'cat-456') {
        return /category\s*4\s*\/\s*5\s*\/\s*6/i.test(text) || /cat\s*4\s*\/\s*5\s*\/\s*6/i.test(text);
      }
      if (groupKey === 'cat-3') {
        return (/category\s*3(?:\s|$|,)/i.test(text) || /cat\s*3(?:\s|$|,)/i.test(text))
          && !/category\s*4\s*\/\s*5\s*\/\s*6/i.test(text);
      }
      if (groupKey === 'cat-2') {
        return (/category\s*2(?:\s|$|,)/i.test(text) || /cat\s*2(?:\s|$|,)/i.test(text));
      }
      if (groupKey === 'cat-1') {
        return (/category\s*1(?:\s|$|,)/i.test(text) || /cat\s*1(?:\s|$|,)/i.test(text))
          && !/cat\s*1\d/i.test(text);
      }
      return false;
    }

    // ===== BLOCK LEVEL (section-level maps on product pages) =====

    secKey(el) {
      if (!el) return '';
      var v = el.getAttribute('data-sec');
      return v ? String(v).trim().toLowerCase() : '';
    }

    groupOfSec(el) {
      var g = el && el.closest('[data-category]');
      return g ? (SVG_TO_GROUP[g.getAttribute('data-category')] || null) : null;
    }

    buildBlockIndex() {
      var self = this;
      this.blockIndex = {};
      this.getOriginalTicketCards().forEach(function(card) {
        var key = (card.getAttribute('data-block') || '').trim().toLowerCase();
        if (!key) return;
        var e = self.blockIndex[key] || (self.blockIndex[key] = { cards: [], min: Infinity, qty: 0, cats: [] });
        e.cards.push(card);
        var cents = parseFloat(card.getAttribute('data-price'));
        if (!isNaN(cents)) e.min = Math.min(e.min, cents / 100);
        var q = parseInt(card.getAttribute('data-qty'), 10);
        if (isNaN(q)) { var qi = card.querySelector('.qty-input'); q = parseInt(qi && qi.max, 10) || 0; }
        e.qty += q;
        var c = (card.getAttribute('data-category') || '').replace(/^category-/, '');
        if (/^\d+$/.test(c) && e.cats.indexOf(c) < 0) e.cats.push(c);
      });
      Object.keys(this.blockIndex).forEach(function(k) { if (self.blockIndex[k].min === Infinity) self.blockIndex[k].min = 0; });
    }

    setupBlocks() {
      if (this.pageMode || !this.svgEl) return;
      var self = this;
      this.secByKey = {};
      Array.prototype.forEach.call(this.svgEl.querySelectorAll('[data-sec]'), function(g) {
        var k = self.secKey(g);
        if (k && !self.secByKey[k]) self.secByKey[k] = g;
      });
      this.buildBlockIndex();
      if (!Object.keys(this.blockIndex).length) return;
      this.injectBlockCss();
      Object.keys(this.blockIndex).forEach(function(key) {
        var g = self.secByKey[key];
        if (!g) return;
        var bi = self.blockIndex[key];
        g.classList.add('has-tix');
        g.setAttribute('tabindex', '0');
        g.setAttribute('role', 'button');
        g.setAttribute('aria-label', (g.getAttribute('data-name') || (t('section', 'Section ') + key)) + ': ' +
          (bi.qty ? bi.qty + ' ' + t('tickets', 'tickets') + ', ' : '') + t('from_plain', 'from') + ' ' + formatMapPrice(bi.min));
        g.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            if (self.activeBlock === key) self.clearSelection(); else self.selectBlock(key);
          }
        });
        g.addEventListener('focus', function() {
          self.showSectionTooltip(g, self.groupOfSec(g), null);
          self.positionTooltipAt(g);
        });
        g.addEventListener('blur', function() { self.hideSvgTooltip(); });
      });
      this.renderBlockPills();
      if (window.matchMedia && window.matchMedia('(hover: hover)').matches) this.bindCardHover();
      document.addEventListener('currency:ready', function() { self.renderBlockPills(); });
    }

    injectBlockCss() {
      if (document.getElementById('gt-blockmap-css')) return;
      var st = document.createElement('style');
      st.id = 'gt-blockmap-css';
      st.textContent =
        '.gt-map .sec.has-tix .addedfill{stroke:#111827;stroke-width:2.4}' +
        '.gt-map .sec.has-tix{outline:none}' +
        '.gt-map .sec.has-tix:focus .addedfill,.gt-map .sec.blk-hot .addedfill{stroke:#111827;stroke-width:3.4;opacity:1;filter:brightness(1.12) drop-shadow(0 0 3px rgba(17,24,39,.6))}' +
        '.gt-map[data-blk] .sec .addedfill{opacity:.18;filter:grayscale(.6)}' +
        '.gt-map[data-blk] .sec .mix,.gt-map[data-blk] .sec .sl{opacity:.3}' +
        '.gt-map[data-blk] .sec.blk-sel .addedfill{opacity:1;filter:none;stroke:#111827;stroke-width:3.4}' +
        '.gt-map[data-blk] .sec.blk-sel .mix,.gt-map[data-blk] .sec.blk-sel .sl{opacity:1}' +
        '.gt-map .gt-pill{pointer-events:none}' +
        '.gt-map .gt-pill rect{fill:#111827;stroke:#ffffff;stroke-width:1.4}' +
        '.gt-map .gt-pill text{fill:#ffffff;font-size:12px;font-weight:700;text-anchor:middle;dominant-baseline:central}' +
        '.gt-map[data-blk] .gt-pill{opacity:.3}.gt-map[data-blk] .gt-pill[data-sel]{opacity:1}' +
        '@media (max-width:768px){.gt-map .gt-pill{display:none}}' +
        '.map-tooltip .gt-tip-hint{font-size:11px;opacity:.8}' +
        '.gt-blk-head{display:flex;align-items:center;justify-content:space-between;gap:10px;margin:0 0 10px;padding:10px 12px;border-radius:10px;background:#111827;color:#ffffff;font-weight:600;font-size:14px;line-height:1.3}' +
        '.gt-blk-head a{color:#ffffff;text-decoration:underline;font-weight:600;font-size:13px;white-space:nowrap;cursor:pointer}' +
        '.gt-blk-divider{margin:14px 0 10px;padding:8px 12px;border-radius:8px;background:#f3f4f6;color:#374151;font-weight:600;font-size:13px;line-height:1.3}' +
        '#seating-map-tickets .ticket-variant-card.gt-pulse{animation:gtPulse 1.4s ease-out 1}' +
        '@keyframes gtPulse{0%{box-shadow:0 0 0 0 rgba(17,24,39,.45)}100%{box-shadow:0 0 0 14px rgba(17,24,39,0)}}';
      document.head.appendChild(st);
    }

    renderBlockPills() {
      if (!this.svgEl) return;
      var NS = 'http://www.w3.org/2000/svg';
      var self = this;
      var old = this.svgEl.querySelector('g.gt-pills');
      if (old) old.parentNode.removeChild(old);
      var layer = document.createElementNS(NS, 'g');
      layer.setAttribute('class', 'gt-pills');
      layer.setAttribute('aria-hidden', 'true');
      this.svgEl.appendChild(layer);
      var vb = this.svgEl.viewBox && this.svgEl.viewBox.baseVal;
      Object.keys(this.blockIndex).forEach(function(key) {
        var g = self.secByKey[key];
        if (!g) return;
        var bb;
        try { bb = g.getBBox(); } catch (e) { return; }
        if (!bb || !bb.width) return;
        var bi = self.blockIndex[key];
        var label = formatMapPrice(bi.min) + (bi.qty ? ' · ' + bi.qty + ' ' + t('left', 'left') : '');
        var pg = document.createElementNS(NS, 'g');
        pg.setAttribute('class', 'gt-pill');
        pg.setAttribute('data-pill', key);
        if (self.activeBlock === key) pg.setAttribute('data-sel', '');
        var r = document.createElementNS(NS, 'rect');
        var tx = document.createElementNS(NS, 'text');
        tx.textContent = label;
        pg.appendChild(r);
        pg.appendChild(tx);
        layer.appendChild(pg);
        var w;
        try { w = tx.getComputedTextLength(); } catch (e2) { w = 0; }
        if (!w) w = label.length * 6.6;
        var h = 18, pw = w + 14;
        var cx = bb.x + bb.width / 2, cy = bb.y + bb.height / 2;
        if (vb && vb.width) {
          cx = Math.min(Math.max(cx, vb.x + pw / 2 + 2), vb.x + vb.width - pw / 2 - 2);
          cy = Math.min(Math.max(cy, vb.y + h / 2 + 2), vb.y + vb.height - h / 2 - 2);
        }
        r.setAttribute('x', (cx - pw / 2).toFixed(1));
        r.setAttribute('y', (cy - h / 2).toFixed(1));
        r.setAttribute('width', pw.toFixed(1));
        r.setAttribute('height', h);
        r.setAttribute('rx', h / 2);
        tx.setAttribute('x', cx.toFixed(1));
        tx.setAttribute('y', (cy + 0.5).toFixed(1));
      });
    }

    positionTooltipAt(el) {
      if (!this.tooltip || !el) return;
      var wrapper = this.tooltip.closest('.seating-map-wrapper');
      if (!wrapper) return;
      var wr = wrapper.getBoundingClientRect();
      var er = el.getBoundingClientRect();
      var tw = this.tooltip.offsetWidth || 0;
      var x = er.left + er.width / 2 - wr.left;
      if (tw && wr.width > tw) x = Math.min(Math.max(x, tw / 2 + 4), wr.width - tw / 2 - 4);
      this.tooltip.style.left = x + 'px';
      this.tooltip.style.top = Math.max(4, er.top - wr.top - (this.tooltip.offsetHeight || 40) - 10) + 'px';
    }

    clearBlockState() {
      this.activeBlock = null;
      if (!this.svgEl) return;
      this.svgEl.removeAttribute('data-blk');
      var self = this;
      Object.keys(this.secByKey || {}).forEach(function(k) { self.secByKey[k].classList.remove('blk-sel'); });
      Array.prototype.forEach.call(this.svgEl.querySelectorAll('.gt-pill[data-sel]'), function(p) { p.removeAttribute('data-sel'); });
    }

    selectBlock(key) {
      if (!this.blockIndex[key]) return;
      this.activeGroup = null;
      this.legendItems.forEach(function(item) { item.classList.remove('active'); });
      this.clearSvgHighlights();
      this.activeBlock = key;
      var self = this;
      if (this.svgEl) {
        this.svgEl.setAttribute('data-blk', key);
        Object.keys(this.secByKey).forEach(function(k) { self.secByKey[k].classList.toggle('blk-sel', k === key); });
        Array.prototype.forEach.call(this.svgEl.querySelectorAll('.gt-pill'), function(p) {
          if (p.getAttribute('data-pill') === key) p.setAttribute('data-sel', ''); else p.removeAttribute('data-sel');
        });
      }
      this.hideSvgTooltip();
      this.showBlockTickets(key, true);
    }

    sortCardList(list) {
      var select = document.getElementById('sidebar-sort-select');
      var v = select ? select.value : '';
      var arr = list.slice();
      if (v === 'price-asc') {
        arr.sort(function(a, b) { return (parseFloat(a.dataset.price) || 0) - (parseFloat(b.dataset.price) || 0); });
      } else if (v === 'price-desc') {
        arr.sort(function(a, b) { return (parseFloat(b.dataset.price) || 0) - (parseFloat(a.dataset.price) || 0); });
      } else {
        arr.sort(function(a, b) { return (parseInt(a.dataset.sortOrder) || 0) - (parseInt(b.dataset.sortOrder) || 0); });
      }
      return arr;
    }

    showBlockTickets(key, scroll) {
      if (!this.ticketsPanel) return;
      var bi = this.blockIndex[key];
      if (!bi) return;
      var self = this;
      var esc = function(s) { return String(s).replace(/[&<>"]/g, function(ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]; }); };
      var groups = [];
      bi.cards.forEach(function(c) { var g = self.detectCardGroup(c.textContent); if (g && groups.indexOf(g) < 0) groups.push(g); });
      var generic = this.getOriginalTicketCards().filter(function(c) {
        if ((c.getAttribute('data-block') || '').trim()) return false;
        var txt = c.textContent.toLowerCase();
        return groups.some(function(g) { return self.matchGroup(txt, g); });
      });
      var groupNames = { 'cat-1': 'Category 1', 'cat-2': 'Category 2', 'cat-3': 'Category 3', 'cat-456': 'Category 4/5/6' };
      var sec = this.secByKey[key];
      var secName = sec ? (sec.getAttribute('data-name') || (t('section', 'Section ') + sec.getAttribute('data-sec'))) : (t('section', 'Section ') + key.toUpperCase());

      this._renderingBlock = true;
      this.ticketsPanel.innerHTML = '';
      var head = document.createElement('div');
      head.className = 'gt-blk-head';
      head.innerHTML = '<span>' + esc(secName) + '</span><a href="#" class="gt-blk-reset" role="button">' + esc(t('show_all', 'Show all tickets')) + '</a>';
      head.querySelector('.gt-blk-reset').addEventListener('click', function(e) { e.preventDefault(); e.stopPropagation(); self.clearSelection(); });
      this.ticketsPanel.appendChild(head);

      var first = null;
      var add = function(list) {
        self.sortCardList(list).forEach(function(card) {
          var clone = card.cloneNode(true);
          clone.classList.add('highlight');
          clone.classList.remove('dimmed');
          self.applyCardCategoryColor(clone);
          var qtyInput = clone.querySelector('.qty-input');
          if (qtyInput) qtyInput.value = Math.min(2, parseInt(qtyInput.max, 10) || 2);
          self.ticketsPanel.appendChild(clone);
          if (!first) first = clone;
        });
      };
      add(bi.cards);
      if (generic.length) {
        var div = document.createElement('div');
        div.className = 'gt-blk-divider';
        div.textContent = t('any_seat', 'Any seat in ') + groups.map(function(g) { return t('gn_' + g, groupNames[g] || g); }).join(' / ');
        this.ticketsPanel.appendChild(div);
        add(generic);
      }
      this._renderingBlock = false;
      this.bindTicketEvents();

      if (scroll) {
        var right = this.ticketsPanel.closest('.seating-map-right');
        if (right) right.scrollTop = 0;
        if (first) {
          first.classList.add('gt-pulse');
          setTimeout(function() { first.classList.remove('gt-pulse'); }, 1500);
          if (window.innerWidth <= 768) head.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    bindCardHover() {
      if (!this.ticketsPanel || this._cardHoverBound) return;
      this._cardHoverBound = true;
      var self = this;
      var cur = null;
      this.ticketsPanel.addEventListener('mouseover', function(e) {
        var card = e.target && e.target.closest ? e.target.closest('.ticket-variant-card') : null;
        if (card === cur) return;
        if (cur) self.cardHoverEnd();
        cur = card;
        if (card) self.cardHoverStart(card);
      });
      this.ticketsPanel.addEventListener('mouseleave', function() {
        if (cur) { self.cardHoverEnd(); cur = null; }
      });
    }

    cardHoverStart(card) {
      var key = (card.getAttribute('data-block') || '').trim().toLowerCase();
      if (!key) return;
      var g = this.secByKey[key];
      if (g) { g.classList.add('blk-hot'); this._hotSec = g; return; }
      // listed section not drawn on this map: light its category instead
      if (this.activeBlock) return;
      var grp = this.detectCardGroup(card.textContent);
      if (grp) { this.highlightSvgSections(grp); this._hotGroup = true; }
    }

    cardHoverEnd() {
      if (this._hotSec) { this._hotSec.classList.remove('blk-hot'); this._hotSec = null; }
      if (this._hotGroup) {
        this._hotGroup = false;
        if (this.activeGroup) this.highlightSvgSections(this.activeGroup); else this.clearSvgHighlights();
      }
    }

    getOriginalTicketCards() {
      var allCards = document.querySelectorAll('.ticket-variant-card');
      return Array.from(allCards).filter(function(card) {
        return !card.closest('#seating-map-tickets');
      });
    }

    applyCardCategoryColor(card) {
      var cardText = card.textContent;
      var group = this.detectCardGroup(cardText);

      if (group) {
        var colorMap = {
          'cat-1': '#9B59B6',
          'cat-2': '#2DD4BF',
          'cat-3': '#F472B6',
          'cat-456': '#F97316'
        };
        var color = colorMap[group];

        if (color) {
          var header = card.querySelector('.category-header');
          if (header) {
            header.style.backgroundColor = color;
            header.style.color = this.getContrastColor(color);
          }
        }
      }
    }

    getContrastColor(hexColor) {
      var r = parseInt(hexColor.slice(1, 3), 16);
      var g = parseInt(hexColor.slice(3, 5), 16);
      var b = parseInt(hexColor.slice(5, 7), 16);
      var luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
    }

    showAllTickets() {
      if (!this.ticketsPanel) return;

      var ticketCards = this.getOriginalTicketCards();
      if (ticketCards.length === 0) {
        this.ticketsPanel.innerHTML =
          '<div class="seating-map-tickets__empty">' +
            '<div class="seating-map-tickets__empty-icon">\uD83C\uDF9F\uFE0F</div>' +
            '<div class="seating-map-tickets__empty-text">No tickets available</div>' +
          '</div>';
        return;
      }

      var self = this;
      this.ticketsPanel.innerHTML = '';
      ticketCards.forEach(function(card) {
        var clone = card.cloneNode(true);
        clone.classList.remove('highlight', 'dimmed');
        self.applyCardCategoryColor(clone);
        var qtyInput = clone.querySelector('.qty-input');
        if (qtyInput) qtyInput.value = 2;
        self.ticketsPanel.appendChild(clone);
      });

      this.bindTicketEvents();
      this.applySidebarSort();
    }

    showGroupTickets(groupKey) {
      if (!this.ticketsPanel) return;

      var self = this;
      var ticketCards = this.getOriginalTicketCards();
      var matchingCards = [];

      ticketCards.forEach(function(card) {
        var cardText = card.textContent.toLowerCase();
        if (self.matchGroup(cardText, groupKey)) {
          matchingCards.push(card);
        }
      });

      if (matchingCards.length === 0) {
        var groupLabels = {
          'cat-1': 'Category 1',
          'cat-2': 'Category 2',
          'cat-3': 'Category 3',
          'cat-456': 'Category 4/5/6'
        };
        var groupLabel = groupLabels[groupKey] || groupKey;
        this.ticketsPanel.innerHTML =
          '<div class="seating-map-tickets__empty">' +
            '<div class="seating-map-tickets__empty-icon">\uD83C\uDF9F\uFE0F</div>' +
            '<div class="seating-map-tickets__empty-text">No ' + groupLabel + ' tickets available</div>' +
          '</div>';
        return;
      }

      this.ticketsPanel.innerHTML = '';
      matchingCards.forEach(function(card) {
        var clone = card.cloneNode(true);
        clone.classList.add('highlight');
        clone.classList.remove('dimmed');
        self.applyCardCategoryColor(clone);
        var qtyInput = clone.querySelector('.qty-input');
        if (qtyInput) qtyInput.value = 2;
        self.ticketsPanel.appendChild(clone);
      });

      this.bindTicketEvents();
      this.applySidebarSort();
    }

    setupSortControls() {
      var self = this;
      var select = document.getElementById('sidebar-sort-select');
      if (!select) return;

      select.addEventListener('change', function() {
        self.applySidebarSort();
      });
    }

    applySidebarSort() {
      if (!this.ticketsPanel) return;
      if (this.activeBlock && !this._renderingBlock) { this.showBlockTickets(this.activeBlock); return; }
      var select = document.getElementById('sidebar-sort-select');
      if (!select) return;

      var value = select.value;
      var cards = Array.from(this.ticketsPanel.querySelectorAll('.ticket-variant-card'));
      if (cards.length === 0) return;

      if (value === 'price-asc') {
        cards.sort(function(a, b) { return (parseFloat(a.dataset.price) || 0) - (parseFloat(b.dataset.price) || 0); });
      } else if (value === 'price-desc') {
        cards.sort(function(a, b) { return (parseFloat(b.dataset.price) || 0) - (parseFloat(a.dataset.price) || 0); });
      } else {
        cards.sort(function(a, b) { return (parseInt(a.dataset.sortOrder) || 0) - (parseInt(b.dataset.sortOrder) || 0); });
      }

      var panel = this.ticketsPanel;
      cards.forEach(function(card) { panel.appendChild(card); });
    }

    bindTicketEvents() {
      if (!this.ticketsPanel) return;

      if (this._ticketPanelClickHandler) {
        this.ticketsPanel.removeEventListener('click', this._ticketPanelClickHandler, true);
      }

      var self = this;

      this._ticketPanelClickHandler = async function(e) {
        var target = e.target.closest('button');
        if (!target) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        var card = target.closest('.ticket-variant-card');
        if (!card) return;

        var qtyInput = card.querySelector('.qty-input');
        var getQuantity = function() { return parseInt(qtyInput ? qtyInput.value : 2) || 2; };
        var maxQty = parseInt(qtyInput ? qtyInput.max : 10) || 10;

        var updateSubtotal = function() {
          var quantity = getQuantity();
          var priceElement = card.querySelector('.price-value');
          var subtotalValue = card.querySelector('[data-subtotal-value]');
          var subtotalQty = card.querySelector('.subtotal-qty');
          var subtotalRow = card.querySelector('.subtotal-row');

          if (priceElement && subtotalValue && subtotalQty) {
            var unitPrice = parseFloat(priceElement.dataset.unitPrice);
            if (isNaN(unitPrice)) {
              unitPrice = parseFloat(priceElement.textContent.replace(/[^0-9.]/g, ''));
            }

            var subtotal = unitPrice * quantity;

            if (subtotalRow) {
              subtotalRow.classList.add('updating');
              setTimeout(function() { subtotalRow.classList.remove('updating'); }, 200);
            }

            subtotalValue.textContent = '$' + subtotal.toLocaleString('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            });
            subtotalQty.textContent = '(' + quantity + ' ticket' + (quantity !== 1 ? 's' : '') + ')';
          }
        };

        // Quantity decrease
        if (target.classList.contains('qty-decrease') || target.classList.contains('minus')) {
          var currentQty = getQuantity();
          if (currentQty > 1 && qtyInput) {
            qtyInput.value = currentQty - 1;
            updateSubtotal();
          }
          return;
        }

        // Quantity increase
        if (target.classList.contains('qty-increase') || target.classList.contains('plus')) {
          var currentQty2 = getQuantity();
          if (currentQty2 < maxQty && qtyInput) {
            qtyInput.value = currentQty2 + 1;
            updateSubtotal();
          }
          return;
        }

        // Add to Cart
        if (target.classList.contains('add-to-cart-btn')) {
          var variantId = target.dataset.variantId;
          var quantity = getQuantity();

          if (!variantId || variantId === 'undefined' || variantId === '') {
            alert('Unable to add to cart: Missing product information. Please refresh the page and try again.');
            return;
          }

          var originalText = target.innerHTML;
          target.disabled = true;
          target.innerHTML = '<span>Adding...</span>';

          try {
            var response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                items: [{
                  id: parseInt(variantId, 10),
                  quantity: quantity
                }]
              })
            });

            var responseData = await response.json().catch(function() { return null; });

            if (response.ok) {
              target.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg> Added!';

              if (window.innerWidth <= 768) {
                window.location.href = '/cart';
                return;
              }

              try {
                var sectionsResponse = await fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + '?sections=cart-drawer');
                var sectionsData = await sectionsResponse.json();

                var cartDrawer = document.querySelector('cart-drawer');
                if (cartDrawer && sectionsData['cart-drawer']) {
                  var tempDiv = document.createElement('div');
                  tempDiv.innerHTML = sectionsData['cart-drawer'];
                  var newDrawerContent = tempDiv.querySelector('cart-drawer');
                  if (newDrawerContent) {
                    cartDrawer.innerHTML = newDrawerContent.innerHTML;
                  }

                  await new Promise(function(resolve) { setTimeout(resolve, 50); });

                  if (typeof cartDrawer.show === 'function') {
                    cartDrawer.show();
                  } else if (typeof cartDrawer.open === 'function') {
                    cartDrawer.open();
                  } else {
                    cartDrawer.setAttribute('open', '');
                    cartDrawer.classList.add('is-open');
                  }
                }

                var cartResponse = await fetch('/cart.js');
                var cartData = await cartResponse.json();
                cartData.sections = sectionsData;
                document.dispatchEvent(new CustomEvent('variant:add', {
                  bubbles: true,
                  detail: { items: [], cart: cartData }
                }));
              } catch (drawerError) {
                console.warn('Cart drawer open failed:', drawerError);
                window.location.href = '/cart';
              }

              setTimeout(function() {
                target.innerHTML = originalText;
                target.disabled = false;
                if (qtyInput) qtyInput.value = 2;
              }, 2000);
            } else {
              var errorMessage = responseData && (responseData.description || responseData.message) || 'Failed to add to cart';
              throw new Error(errorMessage);
            }
          } catch (error) {
            console.error('Error adding to cart:', error);
            var errorText = error.message.includes('variant') || error.message.includes('product')
              ? 'Item unavailable'
              : error.message.toLowerCase().includes('maximum quantity')
                ? 'Max Qty in Cart'
                : 'Error - Try Again';
            target.innerHTML = errorText;
            target.title = error.message;

            setTimeout(function() {
              target.innerHTML = originalText;
              target.disabled = false;
            }, 2000);
          }
        }

        // Buy Now
        if (target.classList.contains('buy-now-btn')) {
          var variantId2 = target.dataset.variantId;
          var quantity2 = getQuantity();

          if (!variantId2 || variantId2 === 'undefined' || variantId2 === '') {
            alert('Unable to proceed: Missing product information. Please refresh the page and try again.');
            return;
          }

          var originalText2 = target.innerHTML;
          target.disabled = true;
          target.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" class="animate-spin"><path d="M12 2v4m0 12v4m10-10h-4M6 12H2"/></svg> Processing...';

          try {
            var response2 = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                items: [{
                  id: parseInt(variantId2, 10),
                  quantity: quantity2
                }]
              })
            });

            if (response2.ok) {
              target.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg> Redirecting...';
              window.location.href = '/checkout';
            } else {
              var responseData2 = await response2.json().catch(function() { return null; });
              var errorMessage2 = responseData2 && (responseData2.description || responseData2.message) || 'Failed to add to cart';
              throw new Error(errorMessage2);
            }
          } catch (error2) {
            console.error('[BuyNow] Error:', error2);
            var buyErrLabel = error2.message.toLowerCase().includes('maximum quantity') ? 'Max Qty in Cart' : 'Error - Try Again';
            target.innerHTML = buyErrLabel;
            target.title = error2.message;
            setTimeout(function() {
              target.innerHTML = originalText2;
              target.disabled = false;
            }, 2000);
          }
        }
      };

      this.ticketsPanel.addEventListener('click', this._ticketPanelClickHandler, true);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { new SeatingMapRWC2027(); });
  } else {
    new SeatingMapRWC2027();
  }
})();
